import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { X, Loader2, Mail, MessageSquare, ChevronRight, ChevronLeft, FileText, Paperclip } from 'lucide-react';
import type { Client, Business } from '@/lib/api';

export type DocumentType = 'invoice' | 'quote' | 'credit_memo' | 'purchase_order';
export type SendMode = 'send' | 'reminder';
type Medium = 'email' | 'sms';
type Step = 'choose' | 'compose';

const DOCUMENT_LABELS: Record<DocumentType, { singular: string; titleCase: string }> = {
  invoice: { singular: 'invoice', titleCase: 'Invoice' },
  quote: { singular: 'quote', titleCase: 'Quote' },
  credit_memo: { singular: 'credit memo', titleCase: 'Credit Memo' },
  purchase_order: { singular: 'purchase order', titleCase: 'Purchase Order' },
};

interface SendTemplateResponse {
  data?: { subject: string; body: string; is_custom: boolean };
  error?: string;
}

export interface SendDocumentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentType: DocumentType;
  documentId: string;
  documentNumber: string;
  noteForClient?: string;
  client: Client | null;
  business: Business | null;
  mode?: SendMode;
  getSendTemplate: (id: string, medium: 'email' | 'sms') => Promise<SendTemplateResponse>;
  onSend: (data: {
    medium: 'email' | 'sms';
    to: string[];
    cc: string[];
    bcc: string[];
    subject: string;
    message: string;
    cc_self: boolean;
  }) => Promise<void>;
}

interface EmailTag {
  email: string;
  id: string;
}

interface PhoneTag {
  phone: string;
  id: string;
}

function EmailTagInput({
  emails,
  onEmailsChange,
  placeholder,
}: {
  emails: EmailTag[];
  onEmailsChange: (emails: EmailTag[]) => void;
  placeholder?: string;
}) {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      addEmail();
    } else if (e.key === 'Backspace' && inputValue === '' && emails.length > 0) {
      removeEmail(emails[emails.length - 1].id);
    }
  };

  const addEmail = () => {
    const email = inputValue.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !emails.some(e => e.email === email)) {
      onEmailsChange([...emails, { email, id: crypto.randomUUID() }]);
      setInputValue('');
    }
  };

  const removeEmail = (id: string) => {
    onEmailsChange(emails.filter(e => e.id !== id));
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmail();
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 min-h-[42px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
      {emails.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-sm text-secondary-foreground"
        >
          {tag.email}
          <button
            type="button"
            onClick={() => removeEmail(tag.id)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        type="email"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={emails.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[120px] bg-transparent outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
}

function PhoneTagInput({
  phones,
  onPhonesChange,
  placeholder,
}: {
  phones: PhoneTag[];
  onPhonesChange: (phones: PhoneTag[]) => void;
  placeholder?: string;
}) {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      addPhone();
    } else if (e.key === 'Backspace' && inputValue === '' && phones.length > 0) {
      removePhone(phones[phones.length - 1].id);
    }
  };

  const normalizePhone = (value: string): string => {
    return value.replace(/\D/g, '');
  };

  const formatPhoneDisplay = (digits: string): string => {
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return digits;
  };

  const isValidPhone = (digits: string): boolean => {
    return /^\d{10}$/.test(digits);
  };

  const addPhone = () => {
    const digits = normalizePhone(inputValue);
    if (digits && isValidPhone(digits) && !phones.some(p => normalizePhone(p.phone) === digits)) {
      onPhonesChange([...phones, { phone: formatPhoneDisplay(digits), id: crypto.randomUUID() }]);
      setInputValue('');
    }
  };

  const removePhone = (id: string) => {
    onPhonesChange(phones.filter(p => p.id !== id));
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addPhone();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setInputValue(value.slice(0, 10));
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 min-h-[42px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
      {phones.map((tag) => (
        <span
          key={tag.id}
          className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-sm text-secondary-foreground"
        >
          +1 {tag.phone}
          <button
            type="button"
            onClick={() => removePhone(tag.id)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <div className="flex items-center flex-1 min-w-[140px]">
        <span className="text-muted-foreground mr-1">+1</span>
        <input
          type="text"
          inputMode="numeric"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={phones.length === 0 ? placeholder : ''}
          className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground"
        />
      </div>
    </div>
  );
}

export function SendDocumentModal({
  open,
  onOpenChange,
  documentType,
  documentId,
  documentNumber,
  noteForClient,
  client,
  business,
  mode = 'send',
  getSendTemplate,
  onSend,
}: SendDocumentModalProps) {
  // Two-step flow state
  const [step, setStep] = useState<Step>('choose');
  const [medium, setMedium] = useState<Medium | null>(null);

  // Form state
  const [toEmails, setToEmails] = useState<EmailTag[]>([]);
  const [toPhones, setToPhones] = useState<PhoneTag[]>([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [ccSelf, setCcSelf] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const labels = DOCUMENT_LABELS[documentType];

  // Check if client has contact info
  const hasEmail = Boolean(
    client?.emails?.some(email => email && email.trim())
  );

  // SMS only supported for US numbers (+1)
  const usPhone = client?.phones?.find(phone => {
    if (!phone || !phone.number || !phone.number.trim()) return false;
    const code = phone.country_code?.replace(/\D/g, '') || '1';
    return code === '1';
  });
  const hasUsPhone = Boolean(usPhone);
  const hasNonUsPhone = Boolean(
    client?.phones?.some(phone => {
      if (!phone || !phone.number || !phone.number.trim()) return false;
      const code = phone.country_code?.replace(/\D/g, '') || '1';
      return code !== '1';
    })
  );

  // Get display text for contact info
  const clientEmail = client?.emails?.find(e => e && e.trim()) || '';
  const clientPhone = usPhone?.number || '';

  // Initialize with client emails/phones when modal opens
  useEffect(() => {
    if (open && client) {
      const clientEmails = (client.emails || [])
        .filter(email => email && email.trim())
        .map(email => ({ email, id: crypto.randomUUID() }));
      setToEmails(clientEmails);

      const clientPhones = (client.phones || [])
        .filter(phone => phone && phone.number && phone.number.trim())
        .map(phone => ({ phone: phone.number, id: crypto.randomUUID() }));
      setToPhones(clientPhones);
    }
  }, [open, client]);

  // Fetch template when medium is selected
  useEffect(() => {
    if (!open || !documentId || !medium) return;

    let cancelled = false;
    setLoadingTemplate(true);

    getSendTemplate(documentId, medium)
      .then((response) => {
        if (cancelled) return;
        if (response.data) {
          if (medium === 'email') {
            setEmailSubject(response.data.subject);
            setEmailMessage(response.data.body);
          } else {
            setSmsMessage(response.data.body);
          }
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to fetch template:', error);
        if (medium === 'email') {
          const businessName = business?.name || 'Your Business';
          setEmailSubject(`${labels.titleCase} #${documentNumber} from ${businessName}`);
          setEmailMessage('');
        } else {
          setSmsMessage('');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingTemplate(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, documentId, medium, getSendTemplate, documentNumber, business?.name, labels.titleCase]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('choose');
      setMedium(null);
      setCcSelf(false);
      setSending(false);
      setLoadingTemplate(false);
    }
  }, [open]);

  const handleSelectMedium = (selected: Medium) => {
    setMedium(selected);
    setStep('compose');
  };

  const handleBack = () => {
    setStep('choose');
    setMedium(null);
  };

  const handleSend = async () => {
    if (!medium) return;
    if (medium === 'email' && toEmails.length === 0) return;
    if (medium === 'sms' && toPhones.length === 0) return;

    const fullMessage = medium === 'email'
      ? (noteForClient ? `${emailMessage}\n\n---\n\n${noteForClient}` : emailMessage)
      : smsMessage;

    setSending(true);
    try {
      await onSend({
        medium,
        to: medium === 'email' ? toEmails.map(e => e.email) : toPhones.map(p => `+1${p.phone.replace(/\D/g, '')}`),
        cc: [],
        bcc: [],
        subject: medium === 'email' ? emailSubject : '',
        message: fullMessage,
        cc_self: medium === 'email' ? ccSelf : false,
      });
      onOpenChange(false);
    } catch (error) {
      console.error(`Failed to send ${labels.singular}:`, error);
    } finally {
      setSending(false);
    }
  };

  const canSend = medium === 'email' ? toEmails.length > 0 : toPhones.length > 0;
  const baseTitle = mode === 'reminder' ? `Send ${labels.titleCase} Reminder` : `Send ${labels.titleCase}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        {step === 'choose' ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">{baseTitle}</DialogTitle>
            </DialogHeader>

            {/* Step 1: Choose Medium */}
            <div className="py-6">
              <p className="text-center text-lg font-medium mb-2">How would you like to send?</p>
              <p className="text-center text-muted-foreground text-sm mb-6">
                {client?.billing_name ? `Sending to ${client.billing_name}` : 'Choose a delivery method'}
              </p>

              <div className="space-y-3">
                {/* Email Option */}
                <button
                  type="button"
                  onClick={() => hasEmail && handleSelectMedium('email')}
                  disabled={!hasEmail}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-colors text-left ${
                    hasEmail
                      ? 'bg-card hover:bg-accent border-border cursor-pointer'
                      : 'bg-muted/50 border-border/50 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${
                    hasEmail ? 'bg-blue-50' : 'bg-muted'
                  }`}>
                    <Mail className={`h-7 w-7 ${hasEmail ? 'text-blue-600' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold ${hasEmail ? 'text-foreground' : 'text-muted-foreground'}`}>
                      Email
                    </p>
                    <p className={`text-sm truncate ${hasEmail ? 'text-muted-foreground' : 'text-muted-foreground/70'}`}>
                      {hasEmail ? `Send to ${clientEmail}` : 'No email on file'}
                    </p>
                  </div>
                  {hasEmail && <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                </button>

                {/* SMS Option */}
                <button
                  type="button"
                  onClick={() => hasUsPhone && handleSelectMedium('sms')}
                  disabled={!hasUsPhone}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-colors text-left ${
                    hasUsPhone
                      ? 'bg-card hover:bg-accent border-border cursor-pointer'
                      : 'bg-muted/50 border-border/50 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${
                    hasUsPhone ? 'bg-emerald-50' : 'bg-muted'
                  }`}>
                    <MessageSquare className={`h-7 w-7 ${hasUsPhone ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold ${hasUsPhone ? 'text-foreground' : 'text-muted-foreground'}`}>
                      Text Message
                    </p>
                    <p className={`text-sm truncate ${hasUsPhone ? 'text-muted-foreground' : 'text-muted-foreground/70'}`}>
                      {hasUsPhone ? `Send to ${clientPhone}` : hasNonUsPhone ? 'US numbers only' : 'No phone on file'}
                    </p>
                  </div>
                  {hasUsPhone && <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                </button>
              </div>

              {/* Document indicator */}
              <div className="flex items-center justify-center gap-2 mt-8 text-muted-foreground text-sm">
                <FileText className="h-4 w-4" />
                <span>{documentNumber} will be attached</span>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Step 2: Compose */}
            <DialogHeader className="flex flex-row items-center gap-2">
              <button
                type="button"
                onClick={handleBack}
                className="p-1 rounded-md hover:bg-accent transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <DialogTitle className="text-xl font-semibold">
                {medium === 'email' ? 'Compose Email' : 'Compose Text'}
              </DialogTitle>
            </DialogHeader>

            {loadingTemplate ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading...</span>
              </div>
            ) : medium === 'email' ? (
              <div className="space-y-4 mt-4">
                {/* To Field */}
                <div className="space-y-2">
                  <Label htmlFor="to" className="text-sm font-medium">To</Label>
                  <EmailTagInput
                    emails={toEmails}
                    onEmailsChange={setToEmails}
                    placeholder="Enter email addresses..."
                  />
                  <div className="flex items-center space-x-2 pt-1">
                    <input
                      type="checkbox"
                      id="cc-self"
                      checked={ccSelf}
                      onChange={(e) => setCcSelf(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <Label
                      htmlFor="cc-self"
                      className="text-sm font-medium leading-none cursor-pointer"
                    >
                      CC myself
                    </Label>
                  </div>
                </div>

                {/* Subject Field */}
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-sm font-medium">Subject</Label>
                  <Input
                    id="subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter subject..."
                  />
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-medium">Message</Label>
                  <Textarea
                    id="message"
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Write your message..."
                    rows={6}
                    className="resize-none"
                  />
                </div>

                {/* Attachment indicator */}
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                    <Paperclip className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{documentNumber}</p>
                    <p className="text-xs text-muted-foreground">{labels.titleCase} PDF will be attached</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4 mt-4">
                {/* To Field */}
                <div className="space-y-2">
                  <Label htmlFor="to-phone" className="text-sm font-medium">To</Label>
                  <PhoneTagInput
                    phones={toPhones}
                    onPhonesChange={setToPhones}
                    placeholder="Enter phone number..."
                  />
                  <p className="text-xs text-muted-foreground">US numbers only (+1)</p>
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <Label htmlFor="sms-message" className="text-sm font-medium">Message</Label>
                  <Textarea
                    id="sms-message"
                    value={smsMessage}
                    onChange={(e) => setSmsMessage(e.target.value)}
                    placeholder="Write your message..."
                    rows={4}
                    className="resize-none"
                  />
                  <p className={`text-xs ${smsMessage.length > 160 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    {smsMessage.length} / 160 characters
                  </p>
                </div>

                {/* Attachment indicator */}
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{documentNumber}</p>
                    <p className="text-xs text-muted-foreground">{labels.titleCase} link will be included</p>
                  </div>
                </div>
              </div>
            )}

            {!loadingTemplate && (
              <>
                <Separator className="my-2" />

                {/* Footer */}
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={sending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSend}
                    disabled={!canSend || sending}
                  >
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : medium === 'email' ? (
                      'Send Email'
                    ) : (
                      'Send SMS'
                    )}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
